import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import { api } from "../utils/api";
import { TrackPicker } from "./TrackPicker";
import type { PickedTrack } from "./TrackPicker";

interface Props {
  customSeriesId: string;
  onClose: () => void;
  onAdded: () => void;
}

const WIND_DIRS = ["", "N", "NE", "E", "SE", "S", "SW", "W", "NW"];

// Add a round/week INTO a custom series (#282): pick a track (iRacing picker) +
// dates + optional weather. Stays open-able so the organizer adds rounds in a loop.
export default function AddWeekSheet({ customSeriesId, onClose, onAdded }: Props) {
  const [picked, setPicked] = useState<PickedTrack | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [withWeather, setWithWeather] = useState(false);
  const [tempF, setTempF] = useState("");
  const [sky, setSky] = useState("20");
  const [windMph, setWindMph] = useState("");
  const [windDir, setWindDir] = useState("");
  const [humidity, setHumidity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string[]>([]);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab") return;
      const list = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])') || []);
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, [onClose, submitting]);

  const toIso = (d: string) => (d ? `${d}T12:00:00Z` : null);
  const badDateOrder = !!startDate && !!endDate && endDate < startDate;
  const canSubmit = !!picked && !badDateOrder && !submitting;

  const submit = async () => {
    if (!canSubmit || !picked) return;
    setSubmitting(true);
    setError(null);
    try {
      const trackName = picked.configName ? `${picked.trackName} - ${picked.configName}` : picked.trackName;
      const week = await api<{ id: string }>("/api/track-weeks/custom", {
        method: "POST",
        body: JSON.stringify({
          customSeriesId,
          trackName,
          trackId: picked.trackId,
          trackConfig: picked.configName,
          category: picked.category,
          startsAt: toIso(startDate),
          endsAt: toIso(endDate),
        }),
      });
      if (withWeather && tempF.trim()) {
        await api(`/api/track-weeks/${week.id}/weather`, {
          method: "PUT",
          body: JSON.stringify({
            tempF: Number(tempF),
            cloudCoverPct: Number(sky),
            humidityPct: humidity.trim() ? Number(humidity) : null,
            windMph: windMph.trim() ? Number(windMph) : null,
            windDirectionCardinal: windDir || null,
          }),
        }).catch(() => undefined);
      }
      onAdded();
      // Stay open for the next round: refresh the series behind us, then reset the
      // per-round inputs (keep the weather toggle + sky default — likely reused).
      setAdded((a) => [trackName, ...a]);
      setPicked(null);
      setStartDate("");
      setEndDate("");
      setTempF("");
      setWindMph("");
      setHumidity("");
      setSubmitting(false);
      dialogRef.current?.querySelector<HTMLInputElement>('input[type="search"], input')?.focus();
    } catch (err: any) {
      setError(err?.message || "Could not add the round.");
      setSubmitting(false);
    }
  };

  return createPortal(
    <div style={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose()}>
      <div ref={dialogRef} className="card" style={styles.modal} role="dialog" aria-modal="true" aria-label="Add a round" tabIndex={-1}>
        <div style={styles.head}>
          <span className="eyebrow">Add a round</span>
          <h2 style={styles.title}>Pick a track</h2>
        </div>

        {added.length > 0 && (
          <div style={styles.addedBanner} role="status">
            Added {added.length} {added.length === 1 ? "round" : "rounds"} — last:{" "}
            <strong>{added[0]}</strong>. Pick another track to keep going, or Done to finish.
          </div>
        )}

        <TrackPicker selected={picked} onSelect={setPicked} />

        {picked && (
          <div style={styles.selected}>
            Selected: <strong>{picked.trackName}{picked.configName ? ` — ${picked.configName}` : ""}</strong>
          </div>
        )}

        <div style={styles.dateRow}>
          <label style={styles.field}>
            <span className="section-label" style={{ margin: 0 }}>Starts</span>
            <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label style={styles.field}>
            <span className="section-label" style={{ margin: 0 }}>Ends</span>
            <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
        </div>
        {badDateOrder && <div style={styles.error}>The end date is before the start date.</div>}

        <div style={styles.weatherToggleRow}>
          <label style={styles.weatherToggle}>
            <input type="checkbox" checked={withWeather} onChange={(e) => setWithWeather(e.target.checked)} />
            <span className="section-label" style={{ margin: 0 }}>Add projected weather</span>
          </label>
          <span style={styles.optional}>optional</span>
        </div>
        {withWeather && (
          <div style={styles.weatherGrid}>
            <label style={styles.field}><span className="section-label" style={{ margin: 0 }}>Air temp °F</span>
              <input className="input" inputMode="numeric" placeholder="78" value={tempF} onChange={(e) => setTempF(e.target.value)} /></label>
            <label style={styles.field}><span className="section-label" style={{ margin: 0 }}>Sky</span>
              <select className="select" value={sky} onChange={(e) => setSky(e.target.value)}>
                <option value="0">Clear</option><option value="20">Partly cloudy</option><option value="55">Mostly cloudy</option><option value="90">Overcast</option>
              </select></label>
            <label style={styles.field}><span className="section-label" style={{ margin: 0 }}>Wind mph</span>
              <input className="input" inputMode="numeric" placeholder="11" value={windMph} onChange={(e) => setWindMph(e.target.value)} /></label>
            <label style={styles.field}><span className="section-label" style={{ margin: 0 }}>Wind dir</span>
              <select className="select" value={windDir} onChange={(e) => setWindDir(e.target.value)}>
                {WIND_DIRS.map((d) => <option key={d || "none"} value={d}>{d || "—"}</option>)}
              </select></label>
            <label style={styles.field}><span className="section-label" style={{ margin: 0 }}>Humidity %</span>
              <input className="input" inputMode="numeric" placeholder="55" value={humidity} onChange={(e) => setHumidity(e.target.value)} /></label>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.actions}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            {added.length ? `Done (${added.length} added)` : "Done"}
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
            {submitting ? "Adding…" : "Add round"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(6,8,12,0.72)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "3.5rem 1rem", overflowY: "auto" },
  modal: { width: "100%", maxWidth: "560px", padding: "1.6rem 1.6rem 1.3rem", display: "flex", flexDirection: "column", gap: "0.9rem" },
  head: { display: "flex", flexDirection: "column", gap: "0.3rem" },
  title: { fontFamily: "var(--font-display)", fontSize: "1.4rem", fontWeight: 700, margin: 0, color: "var(--text-strong)" },
  selected: { fontSize: "0.875rem", color: "var(--text-default)" },
  addedBanner: { fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.45, padding: "0.6rem 0.8rem", borderRadius: "10px", border: "1px solid var(--flag-green)", background: "var(--flag-green-soft)" },
  field: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  dateRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8rem" },
  weatherToggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid var(--rule-faint)", paddingTop: "0.8rem" },
  weatherToggle: { display: "flex", alignItems: "center", gap: "0.55rem", cursor: "pointer" },
  optional: { fontSize: "0.8125rem", color: "var(--text-faint)" },
  weatherGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.8rem" },
  error: { fontSize: "0.875rem", color: "var(--flag-red)", lineHeight: 1.4 },
  actions: { display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "0.5rem" },
};
